/**
 * LandingPage
 *
 * The page displays a LandingPage
 */

import React, { Suspense, useContext } from 'react';

// Components.
import {
  LandingPageFragment,
  ParagraphCallToActionBanner,
} from '~/jobiqo/models/entity';
import { AiGeneratedSiteParagraph } from '~/jobiqo/models/landingAi';
import {
  SimpleTextParagraph,
  SearchBannerParagraph,
  MultiColImageTextParagraph,
  TextWithImageOnSideParagraph,
  FeaturedJobsParagraph,
  FeaturedOrganizationsParagraph,
  HeroSimpleCenteredParagraph,
  FeatureCentered2ColumnsParagraph,
  StatsWithFadingBackgroundParagraph,
  CtaSimpleParagraph,
  HeroSearchBannerFullParagraph,
  HeroSearchBannerBackgroundParagraph,
  FeatureGridWithOffsetIconsParagraph,
  FeatureGridOnBrandParagraph,
  HeroSplitWithScreenParagraph,
  LogoCloudSimpleParagraph,
  CtaSimpleBackgroundParagraph,
  EmbeddedYoutubeParagraph,
  SimpleSearchBarParagraph,
  RssFeedParagraph,
  InsecureScriptParagraph,
  ProductsGridParagraph,
  FacetCategoriesParagraph,
  VideoTeasersParagraph,
  TopJobsParagraph,
  CtaButtonParagraph,
  CardsSliderParagraph,
  HorizontalCardParagraph,
  TrainingOrganizationsParagraph,
  HeroSearchBannerCenteredParagraph,
  TextWithMultipleButtonsParagraph,
  RegionalJobsParagraph,
  HeroVideoSearchBannerParagraph,
} from '~/jobiqo/types/types';
import Layout from '~/jobiqo/components/Layout';
import { FeaturedBlogsParagraph } from '~/jobiqo/components/Landingpage/FeaturedBlogs';
import { LandingPageOrganizations as FeaturedOrganizationsRow } from '~/jobiqo/components/Landingpage/Organizations';
import FeaturedBlogs from '~/jobiqo/components/Landingpage/FeaturedBlogs';
import BlogTeasersRow from '~/jobiqo/components/Landingpage/BlogTeasers';
import CobrandedGateway from '~/jobiqo/components/Landingpage/CobrandedGateway';
import CallToAction from '~/jobiqo/components/Landingpage/CallToAction';
import TextWithImageOnSide from '~/jobiqo/components/Landingpage/TextWithImageOnSide';
import SearchBanner from '~/jobiqo/components/Landingpage/SearchBanner';
import MultiColImageText from '~/jobiqo/components/Landingpage/MultiColImageText';
import SimpleTextRow from '~/jobiqo/components/Landingpage/SimpleTextRow';
import AiGeneratedSiteParagraphRow from '~/jobiqo/components/Landingpage/AiGeneratedSiteParagraph';
import FeaturedJobsRow from '~/jobiqo/components/Landingpage/Jobs';
import HeroSimpleCenteredRow from '~/jobiqo/components/Landingpage/HeroSimpleCentered';
import FeatureCentered2ColumnsRow from '~/jobiqo/components/Landingpage/FeatureCentered2Columns';
import FeatureGridWithOffsetIconsRow from '~/jobiqo/components/Landingpage/FeatureGridWithOffsetIcons';
import FeatureGridOnBrandRow from '~/jobiqo/components/Landingpage/FeatureGridOnBrand';
import StatsFadingBackgroundRow from '~/jobiqo/components/Landingpage/StatsFadingBackground';
import CTASimpleRow from '~/jobiqo/components/Landingpage/CTASimple';
import CTASimpleBackgroundRow from '~/jobiqo/components/Landingpage/CTASimpleBackground';
import HeroSearchBannerFullRow from '~/jobiqo/components/Landingpage/HeroSearchBannerFull';
import HeroSearchBannerBackgroundRow from '~/jobiqo/components/Landingpage/HeroSearchBannerBackground';
import HeroSplitWithScreentRow from '~/jobiqo/components/Landingpage/HeroSplitWithScreen';
import LogoCloudSimpleRow from '~/jobiqo/components/Landingpage/LogoCloudSimple';
import EmbeddedYoutube from '~/jobiqo/components/Landingpage/EmbeddedYoutube';
import SimpleSearchBar from '~/jobiqo/components/Landingpage/SimpleSearchBar';
import FeedCardsRow from '~/jobiqo/components/Landingpage/FeedCards';
import InsecureScript from '~/jobiqo/components/Landingpage/InsecureScript';
import AdUnitRowSlot from '~/jobiqo/components/Slots/AdUnitRowSlot';
import ProductsGridRow from '~/jobiqo/components/Landingpage/Products';
import OrganizationProfileRow from '~/jobiqo/components/Landingpage/OrganizationProfile';
import FacetCategoriesRow from '~/jobiqo/components/Landingpage/FacetCategories';
import VideoTeasersRow from '~/jobiqo/components/Landingpage/VideoTeasersRow';
import TopJobsRow from '~/jobiqo/components/Landingpage/TopJobs';
import CTAButtonRow from '~/jobiqo/components/Landingpage/CTAButton';
import CardsSliderRow from '~/jobiqo/components/Landingpage/CardsSlider';
import HorizontalCardRow from '~/jobiqo/components/Landingpage/HorizontalCardRow';
import HeroSearchBannerCenteredRow from '~/jobiqo/components/Landingpage/HeroSearchBannerCentered';
import { JobTeasers as JobTeasersRow } from '~/jobiqo/components/Landingpage/JobTeasers';
import { OrganizationTeaserLargeProps } from '~/jobiqo/components/Organization/Teaser/Large';
import { withScrollIntoView } from '~/jobiqo/hocs/withScrollIntoView';
import { Box } from '~/vcl/atoms/07-box';
import LandingPageVideoJobs from './VideoJobs';
import TrainingOrganizations from '~/jobiqo/components/Landingpage/TrainingOrganizations';
import TextWithMultipleButtons from '~/jobiqo/components/Landingpage/TextWithMultipleButtons';
import VisualEditorWrapper from './VisualEditor/components/VisualEditorWraper';
import styles from './landingPage.module.scss';
import RegionalJobsRow from '~/jobiqo/components/Landingpage/RegionalJobs';
import HeroVideoBanner from '~/jobiqo/components/Landingpage/HeroVideoBanner';
import { PathContext } from '~/jobiqo/context/app';
import {
  applyVisualEditorPreviews,
  useVisualEditorPreview,
} from './VisualEditor/useVisualEditorPreview';

interface Props {
  /**
   * The entity to display as a Landing page.
   */
  entity: LandingPageFragment;
  /**
   * The path of the page.
   */
  path: string;
}

export const LandingPageBody = ({ entity, path }: Props) => {
  const { querystring } = useContext(PathContext);
  // In the visual editor, sections generated but not saved yet are shown too.
  const previews = useVisualEditorPreview(
    (querystring as any)?.visual_editor === 'true',
  );
  const rows = applyVisualEditorPreviews(
    entity.landingPageRows ?? [],
    previews,
  );

  return (
    <div className={`jd-lp-${path} ${styles.landingPageWrapper}`}>
      <Box
        padding={0}
        margin={0}
        backgroundColorOverride={
          entity.backgroundColor
            ? `var(--color-${entity.backgroundColor})`
            : `var(--color-background)`
        }
        boxShadow={'none'}
      >
        {rows.length > 0 &&
          rows.map((row, rowIndex) => {
            const key = `lp-${entity.id}-${rowIndex}`;

            return (
              <Suspense key={key}>
                <section
                  id={`row-${rowIndex + 1}`}
                  className={`jd-row-${row.bundle}`}
                >
                  <VisualEditorWrapper uuid={row?.uuid ?? ''}>
                    {(() => {
                      {
                        switch (row?.bundle) {
                          case 'call_to_action_banner':
                            return (
                              <CallToAction
                                maxContainerSize={entity.maxContainerSize}
                                rowBanner={row as ParagraphCallToActionBanner}
                                index={rowIndex}
                              />
                            );
                          case 'text_with_image_on_side':
                            return (
                              <TextWithImageOnSide
                                maxContainerSize={entity.maxContainerSize}
                                rowSideText={
                                  row as TextWithImageOnSideParagraph
                                }
                              />
                            );
                          case 'search_banner':
                            return (
                              <SearchBanner
                                index={rowIndex}
                                rowSearchBanner={row as SearchBannerParagraph}
                              />
                            );
                          case 'multi_col_image_text':
                            return (
                              <MultiColImageText
                                maxContainerSize={entity.maxContainerSize}
                                rowImageText={row as MultiColImageTextParagraph}
                              />
                            );
                          case 'simple_text':
                            return (
                              <SimpleTextRow
                                maxContainerSize={entity.maxContainerSize}
                                rowText={row as SimpleTextParagraph}
                              />
                            );
                          case 'ai_generated_site_paragraph':
                            return (
                              <AiGeneratedSiteParagraphRow
                                row={row as AiGeneratedSiteParagraph}
                              />
                            );
                          case 'featured_jobs':
                            return (
                              <FeaturedJobsRow
                                maxContainerSize={entity.maxContainerSize}
                                rowFeaturedJobsInfo={
                                  row as FeaturedJobsParagraph
                                }
                              />
                            );
                          case 'featured_video_jobs':
                            return (
                              <LandingPageVideoJobs
                                maxContainerSize={entity.maxContainerSize}
                                rowFeaturedJobsInfo={
                                  row as FeaturedJobsParagraph
                                }
                              />
                            );
                          case 'featured_organizations':
                            return (
                              <FeaturedOrganizationsRow
                                maxContainerSize={entity.maxContainerSize}
                                rowFeaturedOrgsInfo={
                                  row as FeaturedOrganizationsParagraph
                                }
                              />
                            );
                          case 'hero_simple_centered':
                            return (
                              <HeroSimpleCenteredRow
                                index={rowIndex}
                                maxContainerSize={entity.maxContainerSize}
                                rowBanner={
                                  (row as unknown) as HeroSimpleCenteredParagraph
                                }
                              />
                            );
                          case 'feature_centered_2_columns':
                            return (
                              <FeatureCentered2ColumnsRow
                                maxContainerSize={entity.maxContainerSize}
                                row={
                                  (row as unknown) as FeatureCentered2ColumnsParagraph
                                }
                              />
                            );
                          case 'feature_grid_with_offset_icons':
                            return (
                              <FeatureGridWithOffsetIconsRow
                                maxContainerSize={entity.maxContainerSize}
                                row={
                                  (row as unknown) as FeatureGridWithOffsetIconsParagraph
                                }
                              />
                            );
                          case 'feature_grid_on_brand':
                            return (
                              <FeatureGridOnBrandRow
                                maxContainerSize={entity.maxContainerSize}
                                row={
                                  (row as unknown) as FeatureGridOnBrandParagraph
                                }
                              />
                            );
                          case 'stats_with_fading_background':
                            return (
                              <StatsFadingBackgroundRow
                                maxContainerSize={entity.maxContainerSize}
                                row={
                                  (row as unknown) as StatsWithFadingBackgroundParagraph
                                }
                              />
                            );
                          case 'cta_simple':
                            return (
                              <CTASimpleRow
                                maxContainerSize={entity.maxContainerSize}
                                row={(row as unknown) as CtaSimpleParagraph}
                              />
                            );
                          case 'cta_simple_background':
                            return (
                              <CTASimpleBackgroundRow
                                maxContainerSize={entity.maxContainerSize}
                                row={
                                  (row as unknown) as CtaSimpleBackgroundParagraph
                                }
                              />
                            );
                          case 'hero_search_banner_full':
                            return (
                              <HeroSearchBannerFullRow
                                index={rowIndex}
                                maxContainerSize={entity.maxContainerSize}
                                row={
                                  (row as unknown) as HeroSearchBannerFullParagraph
                                }
                              />
                            );
                          case 'hero_video_search_banner':
                            return (
                              <HeroVideoBanner
                                index={rowIndex}
                                maxContainerSize={entity.maxContainerSize}
                                row={
                                  (row as unknown) as HeroVideoSearchBannerParagraph
                                }
                              />
                            );
                          case 'hero_search_banner_background':
                            return (
                              <HeroSearchBannerBackgroundRow
                                maxContainerSize={entity.maxContainerSize}
                                row={
                                  (row as unknown) as HeroSearchBannerBackgroundParagraph
                                }
                              />
                            );
                          case 'hero_split_with_screen':
                            return (
                              <HeroSplitWithScreentRow
                                index={rowIndex}
                                maxContainerSize={entity.maxContainerSize}
                                row={
                                  (row as unknown) as HeroSplitWithScreenParagraph
                                }
                              />
                            );
                          case 'logo_cloud_simple':
                            return (
                              <LogoCloudSimpleRow
                                maxContainerSize={entity.maxContainerSize}
                                row={
                                  (row as unknown) as LogoCloudSimpleParagraph
                                }
                              />
                            );
                          case 'embedded_youtube':
                            return (
                              <Suspense key={key}>
                                <EmbeddedYoutube
                                  maxContainerSize={entity.maxContainerSize}
                                  row={
                                    (row as unknown) as EmbeddedYoutubeParagraph
                                  }
                                />
                              </Suspense>
                            );
                          case 'simple_search_bar':
                            return (
                              <SimpleSearchBar
                                maxContainerSize={entity.maxContainerSize}
                                row={
                                  (row as unknown) as SimpleSearchBarParagraph
                                }
                              />
                            );
                          case 'rss_feed':
                            return (
                              <FeedCardsRow
                                maxContainerSize={entity.maxContainerSize}
                                row={(row as unknown) as RssFeedParagraph}
                              />
                            );
                          case 'insecure_script':
                            return (
                              <InsecureScript
                                identifier={key}
                                row={
                                  (row as unknown) as InsecureScriptParagraph
                                }
                                maxContainerSize={entity.maxContainerSize}
                              />
                            );
                          case 'row_ad_unit':
                            return (
                              <AdUnitRowSlot row={(row as unknown) as any} />
                            );
                          case 'products_grid':
                            return (
                              <ProductsGridRow
                                maxContainerSize={entity.maxContainerSize}
                                rowProductsGridInfo={
                                  (row as unknown) as ProductsGridParagraph
                                }
                              />
                            );
                          case 'cobranded_gateway':
                            return <CobrandedGateway row={row as any} />;
                          case 'lp_job_teasers':
                            return (
                              <JobTeasersRow
                                row={row as any}
                                maxContainerSize={entity.maxContainerSize}
                              />
                            );
                          case 'blog_teasers':
                            return (
                              <BlogTeasersRow
                                row={row as any}
                                maxContainerSize={entity.maxContainerSize}
                              />
                            );
                          case 'featured_blogs':
                            return (
                              <FeaturedBlogs
                                maxContainerSize={entity.maxContainerSize}
                                rowFeaturedBlogsInfo={
                                  row as FeaturedBlogsParagraph
                                }
                              />
                            );
                          case 'organization_profile':
                            return (
                              <OrganizationProfileRow
                                maxContainerSize={entity.maxContainerSize}
                                row={
                                  (row as unknown) as OrganizationTeaserLargeProps
                                }
                              />
                            );
                          case 'facet_categories':
                            return (
                              <FacetCategoriesRow
                                maxContainerSize={entity.maxContainerSize}
                                row={
                                  (row as unknown) as FacetCategoriesParagraph
                                }
                              />
                            );
                          case 'video_teasers':
                            return (
                              <VideoTeasersRow
                                maxContainerSize={entity.maxContainerSize}
                                row={(row as unknown) as VideoTeasersParagraph}
                              />
                            );
                          case 'top_jobs':
                            return (
                              <TopJobsRow
                                maxContainerSize={entity.maxContainerSize}
                                topJobsInfo={
                                  (row as unknown) as TopJobsParagraph
                                }
                              />
                            );
                          case 'cta_button':
                            return (
                              <CTAButtonRow
                                maxContainerSize={entity.maxContainerSize}
                                row={(row as unknown) as CtaButtonParagraph}
                              />
                            );
                          case 'cards_slider':
                            return (
                              <CardsSliderRow
                                maxContainerSize={entity.maxContainerSize}
                                rowCardsSliderInfo={
                                  (row as unknown) as CardsSliderParagraph
                                }
                              />
                            );
                          case 'horizontal_card':
                            return (
                              <HorizontalCardRow
                                maxContainerSize={entity.maxContainerSize}
                                backgroundColor={
                                  entity.backgroundColor ?? 'background'
                                }
                                row={
                                  (row as unknown) as HorizontalCardParagraph
                                }
                              />
                            );
                          case 'training_organizations':
                            return (
                              <TrainingOrganizations
                                maxContainerSize={entity.maxContainerSize}
                                rowTrainingOrgsInfo={
                                  (row as unknown) as TrainingOrganizationsParagraph
                                }
                              />
                            );
                          case 'hero_search_banner_centered':
                            return (
                              <HeroSearchBannerCenteredRow
                                index={rowIndex}
                                maxContainerSize={entity.maxContainerSize}
                                row={
                                  (row as unknown) as HeroSearchBannerCenteredParagraph
                                }
                              />
                            );
                          case 'text_with_multiple_buttons':
                            return (
                              <TextWithMultipleButtons
                                maxContainerSize={entity.maxContainerSize}
                                row={
                                  (row as unknown) as TextWithMultipleButtonsParagraph
                                }
                              />
                            );
                          case 'regional_jobs':
                            return (
                              <RegionalJobsRow
                                row={row as RegionalJobsParagraph}
                                maxContainerSize={entity.maxContainerSize}
                              />
                            );
                          default:
                            return null;
                        }
                      }
                    })()}
                  </VisualEditorWrapper>
                </section>
              </Suspense>
            );
          })}
      </Box>
    </div>
  );
};

const LandingPage = React.memo(
  withScrollIntoView(({ entity, path }: Props) => {
    return (
      <Layout fixedHeader={entity.fixedHeader} fullWidthContainer={true}>
        <LandingPageBody entity={entity} path={path} />
      </Layout>
    );
  }),
  (prevProps, nextProps) => {
    return prevProps.entity.id === nextProps.entity.id;
  },
);

export default LandingPage;
